import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";
import { FadeUp, SectionHeading } from "./Reveal";

const R = "/projects/riley";

const PhaseLabel = ({ num, title }) => (
  <div className="flex items-center gap-4 mb-6">
    <span className="font-display text-4xl md:text-5xl font-bold text-[#4361C2]">{num}</span>
    <span className="text-xs tracking-[0.3em] uppercase text-ash border-b border-white/15 pb-1">{title}</span>
  </div>
);

const Caption = ({ children }) => (
  <div className="mt-3 text-[10px] tracking-[0.25em] uppercase text-ash">{children}</div>
);

const BeforeAfterSlider = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  return (
    <div
      ref={ref}
      className="relative w-full aspect-[4/3] md:aspect-[16/9] overflow-hidden border border-white/15 select-none touch-none cursor-ew-resize"
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      data-testid="case-study-before-after-slider"
    >
      <img src={`${R}/before.jpg`} alt="Riley Residence before — one story rambler" className="absolute inset-0 w-full h-full object-cover" draggable="false" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src={`${R}/after-front.jpg`} alt="Riley Residence after — completed second story addition" className="absolute inset-0 w-full h-full object-cover" draggable="false" />
      </div>
      <div className="absolute inset-y-0 z-10" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -translate-x-1/2 w-[2px] bg-paper" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-11 w-11 rounded-full bg-paper text-black flex items-center justify-center shadow-lg">
          <MoveHorizontal size={18} strokeWidth={2} />
        </div>
      </div>
      <span className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase bg-black/70 backdrop-blur-sm px-3 py-1.5" data-testid="slider-label-before">Before</span>
      <span className="absolute top-4 right-4 text-[10px] tracking-[0.3em] uppercase bg-[#19254D]/90 backdrop-blur-sm px-3 py-1.5" data-testid="slider-label-after">After</span>
    </div>
  );
};

const FACTS = [
  ["Location", "Bethesda, Maryland"],
  ["Scope", "Second-Story Addition"],
  ["Architect", "With JPC Architects"],
  ["Structure", "Full Roof Framing Plan"],
];

export const CaseStudy = () => (
  <section id="case-study" className="px-6 md:px-12 py-28 md:py-40 bg-surface border-y border-white/10" data-testid="case-study-section">
    <SectionHeading index="Case Study 01 — Design Build" title="Riley Residence" testId="case-study-heading" />

    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-5">
        <dl className="border border-white/15 divide-y divide-white/10" data-testid="case-study-facts">
          {FACTS.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-5 py-4">
              <dt className="text-[10px] tracking-[0.3em] uppercase text-ash">{k}</dt>
              <dd className="text-sm font-medium">{v}</dd>
            </div>
          ))}
        </dl>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-6 md:col-start-7">
        <p className="text-lg md:text-xl font-light leading-relaxed text-paper" data-testid="case-study-intro">
          A one-story rambler, doubled. Working alongside JPC Architects, we designed and built a
          full second floor on an existing home — and when roofing problems surfaced mid-build, we
          engineered a structural roof framing plan on the spot instead of papering over it.
        </p>
        <p className="mt-5 text-sm leading-relaxed text-ash">
          Drawings, permits, framing, finishes — one team carried this house from sketch to
          walkthrough. Drag the slider below to see the transformation.
        </p>
      </FadeUp>
    </div>

    <FadeUp className="mb-20 md:mb-28">
      <PhaseLabel num="01" title="Before / After" />
      <BeforeAfterSlider />
      <Caption>One-story rambler → two-story home — drag to compare</Caption>
    </FadeUp>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="02" title="The Drawings" />
      </FadeUp>
      <FadeUp className="md:col-span-7">
        <div className="border border-white/15 overflow-hidden bg-[#0d1530]">
          <img src={`${R}/plan.jpg`} alt="Structural roof framing plan" loading="lazy" className="w-full invert opacity-80 mix-blend-screen" data-testid="case-study-plan-image" />
        </div>
        <Caption>Structural roof + floor framing plan</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-5 md:mt-24">
        <div className="border border-white/15 overflow-hidden bg-[#0d1530] aspect-[4/5]">
          <img src={`${R}/drawings.jpg`} alt="Architectural elevation drawings" loading="lazy" className="w-full h-full object-cover object-top invert opacity-80 mix-blend-screen" data-testid="case-study-drawings-image" />
        </div>
        <Caption>Elevations + sections — with JPC Architects</Caption>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="03" title="The Build" />
      </FadeUp>
      <FadeUp className="md:col-span-4 md:row-span-2">
        <img src={`${R}/framing-strip.jpg`} alt="Framing progress sequence" loading="lazy" className="w-full border border-white/15 grayscale hover:grayscale-0 transition-all duration-700" data-testid="case-study-framing-image" />
        <Caption>Second-floor framing sequence</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-8">
        <img src={`${R}/construction.jpg`} alt="Second story under construction" loading="lazy" className="w-full border border-white/15 grayscale hover:grayscale-0 transition-all duration-700" data-testid="case-study-construction-image" />
        <Caption>Existing walls opened — second story going up</Caption>
      </FadeUp>
      <FadeUp delay={0.15} className="md:col-span-8 flex items-center">
        <blockquote className="border-l-2 border-[#4361C2] pl-6 md:pl-8 py-2" data-testid="case-study-quote">
          <p className="font-display text-2xl md:text-3xl uppercase font-bold leading-tight tracking-tight">
            "Roofing problems didn't stop the job. They got a structural framing plan."
          </p>
        </blockquote>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="04" title="The Finish" />
      </FadeUp>
      <FadeUp className="md:col-span-7">
        <img src={`${R}/after-rear.jpg`} alt="Completed rear elevation" loading="lazy" className="w-full aspect-[4/3] object-cover border border-white/15 grayscale hover:grayscale-0 transition-all duration-700" data-testid="case-study-after-rear-image" />
        <Caption>Completed rear elevation</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-5 md:mt-24">
        <img src={`${R}/stair.jpg`} alt="New staircase to second floor" loading="lazy" className="w-full aspect-[3/4] object-cover border border-white/15 grayscale hover:grayscale-0 transition-all duration-700" data-testid="case-study-stair-image" />
        <Caption>New stair to the second floor</Caption>
      </FadeUp>
      <FadeUp className="md:col-span-4">
        <img src={`${R}/hall.jpg`} alt="Finished upstairs hallway" loading="lazy" className="w-full aspect-[3/4] object-cover border border-white/15 grayscale hover:grayscale-0 transition-all duration-700" data-testid="case-study-hall-image" />
        <Caption>Upstairs hallway</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-8 md:mt-24">
        <img src={`${R}/room.jpg`} alt="Finished bedroom in the new second story" loading="lazy" className="w-full aspect-[16/10] object-cover border border-white/15 grayscale hover:grayscale-0 transition-all duration-700" data-testid="case-study-room-image" />
        <Caption>New second-story bedroom</Caption>
      </FadeUp>
    </div>
  </section>
);
